import type { FriendShopOffer, FriendShopUser } from "interfaces/friend-shop";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectPopUpManagerV2State } from "redux/slices/pop-up-manager-v2";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import useRewardsById from "hooks/useRewardsById";
import { useEffect } from "react";
import { friendShopAPI } from "redux/apis/friendShop";
import {
	Container,
	CustomButton,
	DataContainer,
	ItemImage,
	Items,
	Offer,
	OfferItem,
	OffersContainer,
	RewardAmount,
	RewardAmountContainer,
	RewardItem,
	SubTitle,
	Title
} from "./styles";
import { useImagePath } from "hooks/useImagePath";
import { friendCenterAssets } from "assets/cdn";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { WsDialogs as IDialogVariants } from "interfaces/dialog/variant";
import { useMediaQuery } from "@mui/material";
import { sortBundleRewards } from "helpers/rewardSort";

interface Props {}

interface ConfirmPopupPayload {
	selectedUser?: FriendShopUser;
	selectedGift?: FriendShopOffer;
}

export const FriendShopConfirmPopup: React.FC<Props> = () => {
	const { getImage } = useImagePath("cdn");
	const { close, open } = usePopUpsV2();
	const dispatch = useAppDispatch();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { stack } = useAppSelector(selectPopUpManagerV2State);
	const popup = [...stack]
		.reverse()
		.find((item) => item.variant === "friend-shop-confirm-popup");
	const payload = (popup?.payload ?? {}) as ConfirmPopupPayload;
	const selectedGift = payload.selectedGift;
	const selectedUser = payload.selectedUser;
	const friendName = selectedUser?.playerName || "Friend";

	const friendRewardItems = useRewardsById(selectedGift?.offer?.rewardId || "");
	const rewardItems = useRewardsById(selectedGift?.gifterBonusRewardId || "");
	const orderedFriendRewardItems = sortBundleRewards(friendRewardItems);
	const orderedRewardItems = sortBundleRewards(rewardItems);
	const priceText = selectedGift?.offer?.price
		? `${selectedGift.offer.price} USD`
		: "";
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const scale = useCalculateScale(
		isPortrait ? 343 + 40 : 910 + 40,
		isPortrait ? 465 + 40 : 410 + 40
	);
	const backgroundImage = getImage(
		isPortrait
			? friendCenterAssets.common.confirmPopupBackground
			: friendCenterAssets.common.confirmPopupBackgroundLandscape
	);

	const { wsStoreBuy } = useWebStoreContext();
	const { openDialog } = useDialogModalRedux();

	const analyticsPayload = {
		event_name: "friend_gift_purchase_dialog",
		event_type: "friend_gift_dialog",
		source: "friend_shop",
		type: selectedUser?.userId,
		offer_id: selectedGift?.id,
		reward_id: `${selectedGift?.offer?.rewardId},${selectedGift?.gifterBonusRewardId}`,
		price_in_cents: Math.round((selectedGift?.offer?.price ?? 0) * 100)
	};

	useEffect(() => {
		reportPlaygamiEvent("tp.web_frontend", {
			...analyticsPayload,
			action: "show"
		});
	}, []);

	const onClose = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			...analyticsPayload,
			action: "close"
		});
		close({ variant: "friend-shop-confirm-popup" });
	};

	const onConfirmPurchase = async () => {
		if (!selectedGift) {
			return;
		}
		reportPlaygamiEvent("tp.web_frontend", {
			...analyticsPayload,
			action: "click_to_start_purchase"
		});
		const invoice = await wsStoreBuy({
			offerId: selectedGift.offer.id,
			friendGiftShopOfferId: selectedGift.id,
			friendUserId: selectedUser?.userId,
			friendName: selectedUser?.playerName
		});
		close({ variant: "friend-shop-confirm-popup" });
		if (invoice) {
			openDialog(IDialogVariants.XSOLLA_PURCHASE, {
				xsollaToken: invoice.xsollaToken,
				xsollaHost: invoice.xsollaHost,
				invoice,
				section: "friend-shop",
				suppressDips: { purchaseSuccess: true },
				onPurchaseSuccess: async (data) => {
					await dispatch(
						friendShopAPI.endpoints.getGiftsFriendShop.initiate(undefined, {
							forceRefetch: true
						})
					);

					open({
						variant: "friend-shop-collect-popup",
						id: "friend-shop-collect-popup",
						replaceIfExists: true,
						payload: {
							selectedUser,
							selectedGift,
							transactionId: data?.purchase_invoice_id?.toString()
						}
					});
				}
			});
		}
	};

	return (
		<Container scale={scale} $backgroundImage={backgroundImage}>
			<CloseIconButton onClose={onClose} />
			<Title>{`You're sending this Web FRIEND BUNDLE
			to ${friendName}`}</Title>
			<DataContainer>
				<OffersContainer>
					<Offer>
						<SubTitle>Your friend gets:</SubTitle>
						<OfferItem>
							<Items>
								{orderedFriendRewardItems.map(
									({ imageUrl, description }, key) => {
										return (
											<RewardItem className="imgWrap" key={key} width={"25%"}>
												<ItemImage src={imageUrl} alt="" />
												<RewardAmountContainer>
													<RewardAmount>{description}</RewardAmount>
												</RewardAmountContainer>
											</RewardItem>
										);
									}
								)}
							</Items>
						</OfferItem>
					</Offer>
					<Offer>
						<SubTitle>And you get:</SubTitle>
						<OfferItem>
							<Items>
								{orderedRewardItems.map(({ imageUrl, description }, key) => {
									return (
										<RewardItem className="imgWrap" key={key} width={"25%"}>
											<ItemImage src={imageUrl} alt="" />
											<RewardAmountContainer>
												<RewardAmount>{description}</RewardAmount>
											</RewardAmountContainer>
										</RewardItem>
									);
								})}
							</Items>
						</OfferItem>
					</Offer>
				</OffersContainer>
				<CustomButton
					variant="contained"
					color="success"
					size="large"
					onClick={onConfirmPurchase}
				>
					{priceText}
				</CustomButton>
			</DataContainer>
		</Container>
	);
};
